import axios from 'axios'

import useLocalStorage from './useLocalStorage'

import type {
  Offer,
  OfferCreatePayload,
  OfferGeneratePromotionalCodesPayload,
  OfferPromotionalCode,
  OfferUpdatePayload
} from '@/types/types'

export default function useOffer() {
  const { user } = useLocalStorage()

  const loading = ref(false)
  const error = ref<Error | null>(null)

  const fetchOffers = async (params: Record<string, unknown> = {}): Promise<Offer[]> => {
    loading.value = true
    error.value = null
    try {
      const { data } = await axios.get(`/merchants/${user.value.merchant.id}/offers`, { params })
      return data.data ?? []
    } catch (err: unknown) {
      if (axios.isAxiosError(err) && err.response?.status === 404) return []
      error.value = err instanceof Error ? err : new Error('Failed to fetch offers')
      throw err
    } finally {
      loading.value = false
    }
  }

  const fetchOffersTotal = async (params: Record<string, unknown> = {}): Promise<number> => {
    const { data } = await axios.get<{ meta: { total: number } }>(`/merchants/${user.value.merchant.id}/offers`, {
      params: { ...params, page: 1, pageSize: 1 }
    })
    return data.meta.total
  }

  const fetchOffer = async (offerId: number): Promise<Offer> => {
    loading.value = true
    error.value = null
    try {
      const { data } = await axios.get(`/merchants/${user.value.merchant.id}/offers/${offerId}`)
      return data.data
    } catch (err: unknown) {
      error.value = err instanceof Error ? err : new Error('Failed to fetch offer')
      throw err
    } finally {
      loading.value = false
    }
  }

  const createOffer = async (payload: OfferCreatePayload): Promise<Offer> => {
    const { data } = await axios.post(`/merchants/${user.value.merchant.id}/offers`, payload)
    return data.data
  }

  const updateOffer = async (offerId: number, payload: OfferUpdatePayload): Promise<Offer> => {
    const { data } = await axios.patch(`/merchants/${user.value.merchant.id}/offers/${offerId}`, payload)
    return data.data
  }

  const endOffer = async (offerId: number): Promise<Offer> => {
    const { data } = await axios.patch(`/merchants/${user.value.merchant.id}/offers/${offerId}`, {
      endDate: new Date().toISOString()
    })
    return data.data
  }

  const deleteOffer = async (offerId: number): Promise<void> => {
    await axios.delete(`/merchants/${user.value.merchant.id}/offers/${offerId}`)
  }

  const generatePromotionalCodes = async (
    offerId: number,
    payload: OfferGeneratePromotionalCodesPayload
  ): Promise<OfferPromotionalCode[]> => {
    const { data } = await axios.post(
      `/merchants/${user.value.merchant.id}/offers/${offerId}/promotional-code`,
      payload
    )
    return data.data ?? []
  }

  const fetchPromotionalCodes = async (
    offerId: number,
    page = 1,
    pageSize = 50
  ): Promise<{
    data: OfferPromotionalCode[]
    meta: { current_page: number; last_page: number; total: number }
  }> => {
    const { data } = await axios.get(`/merchants/${user.value.merchant.id}/offers/${offerId}/promotional-codes`, {
      params: { page, pageSize }
    })
    return data
  }

  return {
    loading,
    error,
    fetchOffers,
    fetchOffersTotal,
    fetchOffer,
    createOffer,
    updateOffer,
    endOffer,
    deleteOffer,
    generatePromotionalCodes,
    fetchPromotionalCodes
  }
}
